import { memo, useEffect, useState } from "react";
import {
  useMobileVerificationContext,
  useNotificationContext,
  usePersonalDetailsContext,
  useSignUpContext,
  useUserDetailsContext,
  useZenithAccountUpgradeContext,
} from "@zenith/context";
import { AppNavigation, AppStepper } from "@zenith/hooks";
import {
  MajorButtonComponent,
  OTPComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import {
  CUSTOMER_SIGN_UP,
  MISMATCH_PURPOSE,
  NON_CUSTOMER_SIGN_UP,
  SUCCESS_API_CODE,
  TWO_FACTOR_PURPOSE,
} from "@zenith/data/constants";
import {
  ACCOUNT_CREATED,
  PROFILE_CREATION,
  SIGN_UP,
  UPGRADE_ACCOUNT,
} from "@zenith/routes/paths";
import {
  OTPVerifyDTO,
  TierApiService,
  UpgradeTierZenithAccountRequestDTO,
} from "@zenith/services/user";
import { useLocation } from "react-router-dom";
import { CallCalling, Sms } from "iconsax-react";
import { maskEmail } from "@zenith/utils/Formaters/maskEmail";
import WalletIcon from "@zenith/assets/icons/WalletIcon";
import { Logo } from "@zenith/shared";

/* eslint-disable-next-line */
export interface SignUpMobileVerificationPageProps {}

const SignUpMobileVerificationPage = memo(function SignUpMobileVerificationPage(
  props: SignUpMobileVerificationPageProps
) {
  const { navigateTo } = AppNavigation();
  const { nextStep, maxStep, activeStep, prevStep } = AppStepper();
  const location = useLocation();
  const {
    zenithAccountUpgradeContext: { accountNumber, isUpgrade },
  } = useZenithAccountUpgradeContext();

  const { setSignUpContext } = useSignUpContext();

  const {
    userDetailsContext: {
      phoneNumber: userPhoneNumber,
      countryCode: userCountryCode,
    },
  } = useUserDetailsContext();

  const {
    mobileVerificationContext: { countryCode, phoneNumber },
    setFirstName,
  } = useMobileVerificationContext();
  const {
    personalDetailsContext: {
      documentPhoneNumber,
      mismatchFoundInDocument,
      email,
    },
  } = usePersonalDetailsContext();
  const { setOpen } = useNotificationContext();
  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  const SMS = "sms";
  const MAIL = "mail";
  const [otpMethod, setOtpMethod] = useState("");
  const [isEmail, setIsEmail] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtpMethod(event.target.value);
  };

  const maskPhoneNumber = (phoneNumber: string): string => {
    return phoneNumber.replace(/(\d{6})\d{4}(\d{3})/, "$1****$2");
  };

  const otpOptions = [
    {
      label: "Via SMS",
      value: SMS,
      subLabel: "Telephone",
      displayValue: maskPhoneNumber(documentPhoneNumber || phoneNumber),
      icon: <CallCalling size={16} />,
    },
    {
      label: "Via Mail",
      value: MAIL,
      subLabel: "Email Address",
      displayValue: maskEmail(email || ""),
      icon: <Sms size={16} />,
    },
  ];

  const handleOTPCall = () => {
    if (otpMethod === SMS) {
      setIsEmail(false);
    } else {
      setIsEmail(true);
    }
    setShowOTP(true);
  };

  useEffect(() => {
    if (!email || email === "N/A") {
      setShowOTP(true);
    }
  }, [email]);

  useEffect(() => {
    if (!countryCode && !phoneNumber && !isUpgrade) {
      handleBackButton();
    }
  });

  const handleOTPSuccess = (data: OTPVerifyDTO) => {
    const xMfaToken = data.xMfa;
    setSignUpContext({ userExternalId: xMfaToken! });

    const request: UpgradeTierZenithAccountRequestDTO = {
      accountNumber: accountNumber,
    };

    if (activeStep >= maxStep - 1 && isUpgrade) {
      TierApiService.upgradeTierZenithAccount(request)
        .then((response) => {
          if ("data" in response && response.data) {
            setFirstName(response.data.firstName as string);
            navigateTo(ACCOUNT_CREATED, {
              state: { accountInfo: response.data },
            });
          }
        })
        .catch((error) => {
          navigateTo(ACCOUNT_CREATED, {
            state: { upgradeError: error.body },
          });
        });
    } else {
      // The phone is verified, so Back mustn't return to the OTP step
      navigateTo(PROFILE_CREATION, { replace: true });
    }
  };

  const handleBackButton = () => {
    if (!isUpgrade) {
      if (showOTP && otpMethod) {
        setShowOTP(false);
      } else if (mismatchFoundInDocument) {
        prevStep();
      } else {
        navigateTo(SIGN_UP);
      }
    } else {
      navigateTo(UPGRADE_ACCOUNT);
    }
  };

  const getOTPPurpose = () => {
    if (isUpgrade) {
      return TWO_FACTOR_PURPOSE;
    }

    if (mismatchFoundInDocument) {
      return MISMATCH_PURPOSE;
    }
    return location?.state?.existingAccount
      ? CUSTOMER_SIGN_UP
      : NON_CUSTOMER_SIGN_UP;
  };

  const renderOtpOptions = () =>
    otpOptions.map((option) => (
      <label
        key={option.value}
        className=" flex-1 text-sm text-neutral-600 md:text-base"
      >
        {option.label}
        <div
          className={`mt-2 flex cursor-pointer items-center gap-4 rounded-lg border p-4 ${
            otpMethod === option.value
              ? "border-primary-300"
              : "border-neutral-300"
          }`}
        >
          <input
            type="radio"
            name="otp-method"
            value={option.value}
            className="accent-primary-300"
            checked={otpMethod === option.value}
            onChange={handleOtpChange}
          />
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-1 text-xs text-neutral-500">
              {option.icon} {option.subLabel}
            </span>
            <span className="text-sm text-neutral-900">
              {option.displayValue}
            </span>
          </div>
        </div>
      </label>
    ));

  const renderButtonsComp = () => (
    <div
      className={`mt-20 flex flex-col items-center justify-center gap-6 sm:flex-row sm:items-center sm:justify-center`}
    >
      <MajorButtonComponent
        id="back-button"
        buttonType="secondary"
        size="large"
        type="button"
        className="h-12 w-large"
        onClick={handleBackButton}
      >
        Back
      </MajorButtonComponent>

      <MajorButtonComponent
        id="verify-otp"
        buttonType="primary"
        size="large"
        type="submit"
        disabled={!otpMethod}
        onClick={handleOTPCall}
      >
        Continue
      </MajorButtonComponent>
    </div>
  );

  return (
    <div className="w-full sm:max-w-md">
      {toasterData.message && toasterData.title && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}
      <div className="absolute top-8 left-1/2 flex -translate-x-1/2 gap-2 lg:hidden">
        <WalletIcon height={40} width={40} className="my-auto" />
        <Logo />
      </div>

      <div className="flex flex-col items-start justify-start">
        <h5 className="mb-6 text-4xl">Mobile verification</h5>
      </div>
      {mismatchFoundInDocument && !showOTP && (
        <div>
          <p className="mb-6 text-sm text-neutral-600 md:text-base">
            Please select a verification method to receive your OTP and complete
            your BVN authentication
          </p>

          <div className="flex flex-col gap-5">{renderOtpOptions()}</div>

          {renderButtonsComp()}
        </div>
      )}
      {showOTP && (
        <OTPComponent
          phoneNumber={userPhoneNumber || phoneNumber}
          externalAccountPhoneNumber={documentPhoneNumber}
          countryCode={userCountryCode || countryCode}
          onVerifySuccessWithData={handleOTPSuccess}
          onGoBack={handleBackButton}
          OTPPurpose={getOTPPurpose()}
          align={"start"}
          email={email}
          emailOnly={isEmail}
        />
      )}
    </div>
  );
});

export default SignUpMobileVerificationPage;
